import { advancePrivateEpoch, privateEpoch, privateEpochActive, requirePrivateEpoch } from './privacyEpoch';

/* Outbox (IndexedDB): typed jobs met dependency-chain en zichtbare status.
   Gedrag per masterprompt: netwerkfout/408/429/5xx → backoff; 400/403 → failed
   (zichtbaar); 401 → pauze; 409/412 → conflict; niets verdwijnt stil. */
export interface OutboxJob {
  mutationId: string;
  privacyEpoch?: string;           // persistent session generation, absent on pre-v11 jobs
  method: string;
  path: string;
  body: unknown;
  deps: string[];                    // mutationIds die eerst moeten slagen
  attempts: number;
  status: "pending" | "inflight" | "failed" | "conflict" | "auth" | "done";
  lastError?: string;
  nextAttemptAt?: number;
  createdAt: number;
  blobKind: string | null;           // "media" ⇒ blob-store bevat de payload
  resultId?: string;                 // server-id na succes (voor dep-substitutie)
}

const DB_NAME = "gorodn10";
const open = (): Promise<IDBDatabase> => new Promise((ok, bad) => {
  const r = indexedDB.open(DB_NAME, 1);
  r.onupgradeneeded = () => {
    r.result.createObjectStore("outbox", { keyPath: "mutationId" });
    r.result.createObjectStore("blobs");
  };
  r.onsuccess = () => {
    r.result.onversionchange = () => r.result.close();
    ok(r.result);
  };
  r.onerror = () => bad(r.error);
});
const req = <T>(r: IDBRequest<T>): Promise<T> => new Promise((ok, bad) => {
  r.onsuccess = () => ok(r.result); r.onerror = () => bad(r.error);
});

/** Wis alle wachtende mutaties en hun blobs.
 *
 *  Bewust legen in plaats van de database verwijderen: `deleteDatabase` wordt
 *  geblokkeerd zolang de app nog een verbinding openhoudt, en dan blijft de
 *  inhoud van een ingetrokken sessie gewoon staan. */
export async function outboxWipe(): Promise<void> {
  advancePrivateEpoch();
  if (retryTimer !== null) { window.clearTimeout(retryTimer); retryTimer = null; }
  const db = await open();
  const tx = db.transaction(["outbox", "blobs"], "readwrite");
  tx.objectStore("outbox").clear();
  tx.objectStore("blobs").clear();
  await new Promise<void>((ok, bad) => {
    tx.oncomplete = () => ok();
    tx.onerror = () => bad(tx.error);
  });
  db.close();
  notify();
}

async function discardIds(ids: string[]): Promise<void> {
  if (!ids.length) return;
  const db = await open();
  try {
    const tx = db.transaction(["outbox", "blobs"], "readwrite");
    for (const id of ids) { tx.objectStore("outbox").delete(id); tx.objectStore("blobs").delete(id); }
    await new Promise<void>((ok, bad) => { tx.oncomplete = () => ok(); tx.onerror = () => bad(tx.error); });
  } finally { db.close(); }
}

export async function outboxAdd(job: OutboxJob, blob: Blob | null, epoch = privateEpoch()): Promise<void> {
  requirePrivateEpoch(epoch);
  const db = await open();
  try {
    requirePrivateEpoch(epoch);
    const tx = db.transaction(["outbox", "blobs"], "readwrite");
    tx.objectStore("outbox").put({ ...job, privacyEpoch: epoch });
    if (blob) tx.objectStore("blobs").put(blob, job.mutationId);
    await new Promise<void>((ok, bad) => { tx.oncomplete = () => ok(); tx.onerror = () => bad(tx.error); });
  } finally { db.close(); }
  if (!privateEpochActive(epoch)) {
    // A cross-tab wipe can race an already-open IndexedDB transaction.
    await discardIds([job.mutationId]);
    requirePrivateEpoch(epoch);
  }
  notify();
}

export async function outboxAll(): Promise<OutboxJob[]> {
  const epoch = privateEpoch();
  if (!privateEpochActive(epoch)) return [];
  const db = await open();
  let all: OutboxJob[];
  try { all = await req<OutboxJob[]>(db.transaction("outbox").objectStore("outbox").getAll()); }
  finally { db.close(); }
  if (!privateEpochActive(epoch)) return [];
  const stale = all.filter(job => job.privacyEpoch && job.privacyEpoch !== epoch);
  await discardIds(stale.map(job => job.mutationId));
  if (!privateEpochActive(epoch)) return [];
  return all.filter(job => !job.privacyEpoch || job.privacyEpoch === epoch);
}
export async function outboxCount(): Promise<number> {
  return (await outboxAll()).filter(j => j.status !== "done").length;
}

async function update(job: OutboxJob, epoch: string): Promise<void> {
  requirePrivateEpoch(epoch);
  const db = await open();
  try {
    requirePrivateEpoch(epoch);
    const tx = db.transaction("outbox", "readwrite");
    tx.objectStore("outbox").put({ ...job, privacyEpoch: epoch });
    await new Promise<void>((ok, bad) => {
      tx.oncomplete = () => ok(); tx.onerror = () => bad(tx.error);
      tx.onabort = () => bad(tx.error ?? new Error("outbox transaction aborted"));
    });
  } finally { db.close(); }
  if (!privateEpochActive(epoch)) { await discardIds([job.mutationId]); requirePrivateEpoch(epoch); }
  notify();
}
async function getBlob(id: string): Promise<Blob | null> {
  const db = await open();
  try { return (await req(db.transaction("blobs").objectStore("blobs").get(id))) ?? null; }
  finally { db.close(); }
}

/** Verwijder afgeronde jobs zodra geen nog-open afhankelijke job hun
 * resultaat-ID meer nodig heeft. Daarmee verdwijnen ook geüploade privéblobs
 * in plaats van onbeperkt in IndexedDB te blijven staan. */
async function pruneDone(): Promise<void> {
  const all = await outboxAll();
  const needed = new Set(all.filter(j => j.status !== "done").flatMap(j => j.deps));
  const ids = all.filter(j => j.status === "done" && !needed.has(j.mutationId))
    .map(j => j.mutationId);
  if (!ids.length) return;
  const db = await open();
  const tx = db.transaction(["outbox", "blobs"], "readwrite");
  for (const id of ids) {
    tx.objectStore("outbox").delete(id);
    tx.objectStore("blobs").delete(id);
  }
  await new Promise<void>((ok, bad) => {
    tx.oncomplete = () => ok();
    tx.onerror = () => bad(tx.error);
  });
  db.close();
  notify();
}

const listeners = new Set<() => void>();
export function onOutboxChange(fn: () => void): () => void {
  listeners.add(fn); return () => listeners.delete(fn);
}
const notify = () => listeners.forEach(f => f());

/** Zet een afgewezen/conflicterende job en zijn wachtende vervolgjobs opnieuw
 * klaar. De gebruiker hoeft daarvoor nooit IndexedDB te begrijpen. */
export async function outboxRetry(id: string): Promise<void> {
  const epoch = privateEpoch();
  requirePrivateEpoch(epoch);
  const all = await outboxAll();
  const wanted = new Set([id]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const job of all) if (!wanted.has(job.mutationId) && job.deps.some(d => wanted.has(d))) {
      wanted.add(job.mutationId); changed = true;
    }
  }
  for (const job of all.filter(j => wanted.has(j.mutationId) && j.status !== "done")) {
    job.status = "pending"; job.attempts = 0;
    delete job.lastError; delete job.nextAttemptAt;
    await update(job, epoch);
  }
  void outboxFlush();
}

/** Verwijder een job én vervolgjobs die zonder zijn resultaat nooit kunnen
 * slagen. Ook bijbehorende privéblobs verdwijnen. */
export async function outboxDismiss(id: string): Promise<void> {
  const all = await outboxAll();
  const doomed = new Set([id]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const job of all) if (!doomed.has(job.mutationId) && job.deps.some(d => doomed.has(d))) {
      doomed.add(job.mutationId); changed = true;
    }
  }
  const db = await open();
  const tx = db.transaction(["outbox", "blobs"], "readwrite");
  for (const key of doomed) {
    tx.objectStore("outbox").delete(key);
    tx.objectStore("blobs").delete(key);
  }
  await new Promise<void>((ok, bad) => {
    tx.oncomplete = () => ok(); tx.onerror = () => bad(tx.error);
    tx.onabort = () => bad(tx.error ?? new Error("outbox transaction aborted"));
  });
  db.close(); notify();
}

let flushing = false;
let retryTimer: number | null = null;
function retry(job: OutboxJob): void {
  job.attempts++;
  job.nextAttemptAt = Date.now() + Math.min(5 * 60_000, 2_000 * 2 ** Math.min(job.attempts - 1, 7));
}
function scheduleRetry(at: number, epoch: string): void {
  if (!privateEpochActive(epoch)) return;
  if (retryTimer !== null) window.clearTimeout(retryTimer);
  retryTimer = window.setTimeout(() => {
    retryTimer = null;
    if (navigator.onLine && privateEpochActive(epoch)) void outboxFlush();
  }, Math.max(250, at - Date.now()));
}
export async function outboxFlush(): Promise<void> {
  const epoch = privateEpoch();
  if (flushing || !privateEpochActive(epoch)) return;
  flushing = true;
  try {
    const initial = await outboxAll();
    if (!privateEpochActive(epoch)) return;
    // Preserve old unscoped jobs, but require the existing explicit retry/drop
    // choice before sending them under the current session.
    for (const legacy of initial.filter(job => !job.privacyEpoch && job.status === 'pending')) {
      legacy.status = 'failed';
      legacy.lastError = 'Saved by an older app. Review this pending action before retrying.';
      await update(legacy, epoch);
    }
    const jobs = (await outboxAll())
      .filter(j => j.status === "pending" && (j.nextAttemptAt ?? 0) <= Date.now())
      .sort((a, b) => a.createdAt - b.createdAt);
    const done = new Map((await outboxAll())
      .filter(j => j.status === "done").map(j => [j.mutationId, j.resultId ?? ""]));
    const terminal = new Map((await outboxAll())
      .filter(j => j.status === "failed" || j.status === "conflict")
      .map(j => [j.mutationId, j.status]));
    for (const job of jobs) {
      if (!privateEpochActive(epoch)) return;
      const badDep = job.deps.find(d => terminal.has(d));
      if (badDep) {
        job.status = terminal.get(badDep) === "conflict" ? "conflict" : "failed";
        job.lastError = `dependency ${badDep} ${terminal.get(badDep)}`;
        await update(job, epoch); terminal.set(job.mutationId, job.status); continue;
      }
      if (job.deps.some(d => !done.has(d))) continue;      // wacht op afhankelijkheid
      /* dep-substitutie: tijdelijke media-id vervangen door server-id */
      let body = job.body;
      if (body && typeof body === "object") {
        const s = JSON.stringify(body).replace(/"tmp:([^"]+)"/g,
          (_, dep) => `"${done.get(dep) ?? `tmp:${dep}`}"`);
        body = JSON.parse(s);
      }
      try {
        let r: Response;
        if (job.blobKind === "media") {
          const blob = await getBlob(job.mutationId);
          if (!privateEpochActive(epoch)) return;
          if (!blob) { job.status = "failed"; job.lastError = "blob verloren"; await update(job, epoch); continue; }
          const fd = new FormData();
          fd.append("file", blob, "offline-upload");
          r = await fetch("/api/v1/media", { method: "POST", credentials: "same-origin",
            headers: { "idempotency-key": job.mutationId }, body: fd });
        } else {
          r = await fetch("/api/v1" + job.path, {
            method: job.method, credentials: "same-origin",
            headers: { "content-type": "application/json", "idempotency-key": job.mutationId },
            body: body === null || body === undefined ? null : JSON.stringify(body),
          });
        }
        if (!privateEpochActive(epoch)) return;
        if (r.ok) {
          const data = await r.json().catch(() => ({}));
          job.status = "done"; job.resultId = data.id ?? undefined;
          delete job.nextAttemptAt;
          done.set(job.mutationId, job.resultId ?? "");
          await update(job, epoch);
        } else if (r.status === 401) {
          /* Een 401 hier betekent hetzelfde als een 401 elders: deze sessie
             bestaat niet meer. Voorheen bleef het bij een lokale «auth»-status
             en draaide de app gewoon door -- terwijl de outbox precies de
             inhoud bevat die van deze ingetrokken sessie is. Dus melden we het
             op dezelfde centrale plek als api.ts. */
          job.status = "auth"; await update(job, epoch);
          const { revokeSession } = await import("./session");
          revokeSession();
          break;
        }
        else if (r.status === 409 || r.status === 412) { job.status = "conflict"; await update(job, epoch); }
        else if (r.status === 408 || r.status === 429 || r.status >= 500) {
          retry(job); await update(job, epoch); scheduleRetry(job.nextAttemptAt!, epoch); break;
        } else {                                            // 4xx: zichtbaar failed
          job.status = "failed";
          job.lastError = `${r.status}`;
          await update(job, epoch);
        }
      } catch {
        if (!privateEpochActive(epoch)) return;
        retry(job); await update(job, epoch); scheduleRetry(job.nextAttemptAt!, epoch); break;
      }
    }
    if (!privateEpochActive(epoch)) return;
    await pruneDone();
    const next = (await outboxAll())
      .filter(j => j.status === "pending" && j.nextAttemptAt)
      .sort((a, b) => a.nextAttemptAt! - b.nextAttemptAt!)[0];
    if (next) scheduleRetry(next.nextAttemptAt!, epoch);
  } catch (error) {
    if (privateEpochActive(epoch)) throw error;
  } finally { flushing = false; }
}

addEventListener("online", () => void outboxFlush());
